import { TextField } from "@mui/material";
import React, { useEffect, useState } from "react";
import {
  Card,
  Stack,
  Row,
  Col,
  OverlayTrigger,
  Tooltip,
  Button,
} from "react-bootstrap";
import { MdClear, MdModeEdit } from "react-icons/md";
import { MatDataGridWithPagination } from "../../../components/common/dataGrid/DataGridWithPagination";
import { toast } from "react-toastify";
import LoaderMu from "../../../components/common/LoaderMu";
import { LuBuilding2 } from "react-icons/lu";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  propertyTypeListingApi,
  updatePropertyTypeAmakenNativeApi,
  updatePropertyTypeStatus,
} from "../../../services/propertyType";
import { nameBasedRoutes } from "../Routes";
import { Switch } from "antd";
import { useSelector } from "react-redux";
import NotAuthorized from "../../../components/common/NotAuthorized";
export const List = () => {
  // CHECK IF USER IS ADMIN
  const isAdmin = useSelector((state) =>
    state?.adminData?.authorities?.includes("ROLE_ADMIN")
  );

  // MANAGING USER PERMISSIONS HERE
  const roleDetails = useSelector(
    (state) => state?.rolesData?.masterPermissionResponseDTOList
  );
  const [isAuthorized, setIsAuthorized] = useState(false);
  const [authPermissions, setAuthPermissions] = useState(null);

  useEffect(() => {
    if (roleDetails) {
      const permissions = roleDetails.find((detail) => {
        return detail.childPermissions.find(
          (childPerm) =>
            childPerm?.key === "property_type_list" &&
            childPerm?.status === true
        );
      });
      if (permissions) {
        setIsAuthorized(true);
        // Create an object from childPermissions
        const permissionsObject = {};
        permissions?.childPermissions.forEach((permission) => {
          permissionsObject[permission.title.toLowerCase()] = permission.status;
        });
        setAuthPermissions(permissionsObject);
      } else {
        setIsAuthorized(false);
      }
    } else {
      setIsAuthorized(true);
    }
  }, [roleDetails]);

  // FOR MANAGING PAGINATION USING URL
  const location = useLocation();

  const params = new URLSearchParams(location?.search);

  const currentPage = params.get("page") ?? 0;

  const search = params.get("search") ?? "";

  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [propertyTypeList, setPropertyTypeList] = useState([]);
  const [searchValue, setSearchValue] = useState(search ?? "");
  const [pageSize, setPageSize] = useState(10);
  const [totalResults, setTotalResults] = useState(0);
  const [sortingData, setSortingData] = useState({});

  // THIS IS FOR SEARCHING
  const handleValueChange = (value) => {
    setSearchValue(value);
    if (value !== "") {
      navigate(
        `${nameBasedRoutes.List?.path}?page=${currentPage}&search=${encodeURIComponent(value)}`
      );
    } else {
      navigate(`${nameBasedRoutes.List?.path}?page=${currentPage}`);
    }
  };

  // THIS IS FOR PAGE CHANGE
  const handlePageChange = (value) => {
    navigate(
      `${nameBasedRoutes.List?.path}?page=${value?.page}${search !== "" ? "&search=" + search : ""}`
    );
    setPageSize(value?.pageSize);
  };

  // HANDLE SORT MODAL CHANGE
  const handleSortModelChange = (data) => {
    setSortingData(data?.length > 0 ? data[0] : {});
  };

  // CLEAR SEARCH INPUT BOX
  const handleClear = (value) => {
    setSearchValue("");
    if (search !== "") {
      navigate(`${nameBasedRoutes?.List?.path}?page=${currentPage}`);
    }
  };

  // UPDATE STATUS
  const handleActiveStatus = (value, id) => {
    setLoading(true);

    updatePropertyTypeStatus(id, { status: value ?? false })
      .then((response) => {
        setLoading(false);
        fetchPropertyList();
        toast.success(response?.message ?? "Status updated!");
      })
      .catch((error) => {
        setLoading(false);
        if (error?.status == 500) {
          toast.error(
            "Unauthorized or server issue. Please refresh or try later. "
          );
        } else {
          toast.error(
            error?.error_description
              ? error?.error_description
              : error?.details
                ? error?.details
                : error?.message
          );
        }
      });
  };

  // UPDATE AMAKEN NATIVE FLAG (ADMIN ONLY)
  const handleAmakenNativeStatus = (value, id) => {
    setLoading(true);

    updatePropertyTypeAmakenNativeApi(id, value ?? false)
      .then((response) => {
        setLoading(false);
        fetchPropertyList();
        toast.success(response?.message ?? "Amaken native flag updated!");
      })
      .catch((error) => {
        setLoading(false);
        if (error?.status == 500) {
          toast.error(
            "Unauthorized or server issue. Please refresh or try later. "
          );
        } else {
          toast.error(
            error?.error_description
              ? error?.error_description
              : error?.details
                ? error?.details
                : error?.message
          );
        }
      });
  };

  // COLUMNS FOR DATA TABLE
  const columns = [
    {
      field: "id",
      headerName: "S. No.",
      editable: false,
      flex: 0.05,
      minWidth: 100,
      sortable: false,
      renderCell: (params) =>
        params.api.getAllRowIds().indexOf(params.id) +
        1 +
        currentPage * pageSize,
    },
    {
      field: "title",
      headerName: "Property Type (English)",
      editable: false,
      flex: 0.6,
      minWidth: 180,
      sortable: false,
      renderCell: (params) => <span>{params?.row?.title?.en}</span>,
    },
    {
      field: "title_ar",
      headerName: "Property Type (Arabic)",
      editable: false,
      flex: 0.6,
      minWidth: 180,
      sortable: false,
      renderCell: (params) => <span>{params?.row?.title?.ar}</span>,
    },
    {
      field: "propertyCategory",
      headerName: "Category",
      editable: false,
      flex: 0.2,
      minWidth: 120,
      sortable: true,
    },
    {
      field: "status",
      headerName: "Status",
      editable: false,
      flex: 0.2,
      minWidth: 170,
      sortable: false,
      renderCell: (props) => {
        return (
          <Switch
            checked={props?.row?.status ?? false}
            checkedChildren={"ENABLED"}
            unCheckedChildren={"DISABLED"}
            className="fw-bold"
            onChange={(value) => {
              handleActiveStatus(value, props?.row?.id);
            }}
          />
        );
      },
    },
    {
      field: "isAmakenNative",
      headerName: "Amaken Native",
      editable: false,
      flex: 0.2,
      minWidth: 180,
      sortable: false,
      renderCell: (props) => {
        if (!isAdmin) {
          return <span>-</span>;
        }
        return (
          <Switch
            checked={props?.row?.isAmakenNative ?? false}
            checkedChildren={"TRUE"}
            unCheckedChildren={"FALSE"}
            className="fw-bold"
            onChange={(value) => {
              handleAmakenNativeStatus(value, props?.row?.id);
            }}
          />
        );
      },
    },
    {
      field: "action",
      headerName: "Action",
      editable: false,
      flex: 0.2,
      minWidth: 250,
      sortable: false,
      renderCell: (params) => {
        return (
          <Stack
            direction="horizontal"
            gap={3}
            className="justify-content-center"
          >
            {isAdmin ||
            (authPermissions !== null && authPermissions?.edit) === true ? (
              <OverlayTrigger
                placement="top"
                overlay={<Tooltip id="edit-tooltip">Edit</Tooltip>}
              >
                <Button
                  onClick={() =>
                    navigate(
                      `${nameBasedRoutes?.List?.path}/edit/${params?.row?.id}`
                    )
                  }
                  variant="link"
                  className="p-0 text-light"
                >
                  <MdModeEdit />
                </Button>
              </OverlayTrigger>
            ) : (
              ""
            )}
            {isAdmin ||
            (authPermissions !== null &&
              authPermissions?.propertydetails === true) ? (
              <button
                className="btn btn-sm btn-primary"
                onClick={() =>
                  navigate(
                    `${nameBasedRoutes?.detailsConfig?.path}/${params?.row?.id}`
                  )
                }
              >
                Details
              </button>
            ) : (
              ""
            )}
            {isAdmin ||
            (authPermissions !== null &&
              authPermissions?.amenities === true) ? (
              <button
                className="btn btn-sm btn-primary"
                onClick={() =>
                  navigate(
                    `${nameBasedRoutes?.amenitiesConfig?.path}/${params?.row?.id}`
                  )
                }
              >
                Amenities
              </button>
            ) : (
              ""
            )}
          </Stack>
        );
      },
    },
  ];

  // GET DATA LIST FOR TABLE
  const fetchPropertyList = () => {
    setLoading(true);
    // ?search=&page=0&size=10&sort=createdDate,DESC
    const params = {
      page: currentPage ?? 0,
      size: pageSize ?? 10,
      search: searchValue ?? "",
    };
    if (sortingData && Object.keys(sortingData).length !== 0) {
      params["sort"] =
        `${sortingData?.field},${sortingData?.sort?.toUpperCase()}`;
    }
    propertyTypeListingApi(params)
      .then((response) => {
        setLoading(false);
        setPropertyTypeList(response?.data);
        setTotalResults(response?.headers["x-total-count"]);
      })
      .catch((error) => {
        setLoading(false);
        if (error?.status == 500) {
          toast.error(
            "Unauthorized or server issue. Please refresh or try later. "
          );
        } else {
          toast.error(
            error?.error_description
              ? error?.error_description
              : error?.details
                ? error?.details
                : error?.message
          );
        }
      });
  };

  useEffect(() => {
    // CALLING FUNCTION FOR FETCH DATA
    if (isAuthorized) {
      fetchPropertyList();
    } else {
      setLoading(false);
    }
  }, [currentPage, pageSize, searchValue, sortingData, isAuthorized]);

  return (
    <div className="d-flex flex-column pageContainer p-3 h-100">
      <LoaderMu loading={loading} />
      <div className="flex-grow-1 h-100 pageContent position-relative pt-4 overflow-auto">
        {!isAuthorized && !loading ? (
          <NotAuthorized />
        ) : (
          <Card className="h-100 bg-white shadow border-0 theme-card-cover">
            <div className="theme-card-header px-1">
              <Stack
                direction="horizontal"
                gap={2}
                className="flex-wrap justify-content-between px-3 pt-3 pb-2 align-items-start"
              >
                <h5 className="mb-0 position-relative fs-18 text-black fw-medium">
                  <div className="align-items-center bg-primary d-inline-flex h-60 justify-content-center  position-absolute rounded start-0 text-white theme-icon-box w-60 z-1">
                    <LuBuilding2 size={40} />
                  </div>
                  Property Type
                </h5>
                <Stack direction="horizontal" gap={2}>
                  {isAdmin ? (
                    <Link
                      className="btn btn-outline-primary"
                      to={nameBasedRoutes?.mappings?.path}
                    >
                      Mappings
                    </Link>
                  ) : (
                    ""
                  )}
                  {isAdmin ||
                  (authPermissions !== null && authPermissions?.add === true) ? (
                    <Link
                      className="btn btn-primary"
                      to={"/app/property-type/add"}
                      variant="primary"
                    >
                      Add New
                    </Link>
                  ) : (
                    ""
                  )}
                </Stack>
              </Stack>
            </div>
            <div className="flex-grow-1 d-flex flex-column px-3 h-100 overflow-auto">
              <div className="theme-data-table-filter px-1">
                <Row className="justify-content-end">
                  <Col sm={5} lg={2} className="mb-3 pb-1">
                    <TextField
                      id="search-filter"
                      label="Search"
                      variant="standard"
                      fullWidth
                      value={searchValue}
                      onChange={(e) => {
                        handleValueChange(e.target.value);
                      }}
                    />
                    <MdClear
                      className="clear-postion"
                      onClick={handleClear}
                    ></MdClear>
                  </Col>
                </Row>
              </div>

              <MatDataGridWithPagination
                controlId={"id"}
                columns={columns}
                data={propertyTypeList ?? []}
                onPaginationModelChange={handlePageChange}
                pageSize={pageSize}
                totalResults={totalResults}
                currentPage={currentPage}
                handleSortModelChange={handleSortModelChange}
                // totalPages={totalPages}
                // rowsPerPage={rowPerPage}
              />
            </div>
          </Card>
        )}
      </div>
    </div>
  );
};
